import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Entity, generateEntityId } from '../entities/entity';
import { WA_PHYSICS, type Vector2 } from '../core/constants';
import { getGameRNG } from '../core/rng';

const SUPER_SHEEP_FLIGHT_SPEED = 200;
const SUPER_SHEEP_TURN_RATE = 180;
const SUPER_SHEEP_MAX_FUEL = 10;
const SUPER_SHEEP_EXPLOSION_RADIUS = 100;
const SUPER_SHEEP_MAX_DAMAGE = 100;

export class SuperSheep implements Entity {
  id: string;
  position: Vector2;
  velocity: Vector2 = { x: 0, y: 0 };
  active = true;

  facing: 'left' | 'right';
  ownerTeamId: string;

  isGrounded = false;
  walkSpeed = 100;
  jumpVelocity = 350;
  bounciness = 0.6;

  isFlying = false;
  flyingAngle = 0;
  flightFuel = SUPER_SHEEP_MAX_FUEL;

  steeringLeft = false;
  steeringRight = false;

  explosionRadius = SUPER_SHEEP_EXPLOSION_RADIUS;
  maxDamage = SUPER_SHEEP_MAX_DAMAGE;

  private lifetime = 10;
  private jumpCooldown = 0;
  private randomJumpTimer = 0;

  onExplode?: (x: number, y: number) => void;
  onBaa?: () => void;
  onFlyStart?: () => void;
  onFlyEnd?: () => void;

  constructor(x: number, y: number, facing: 'left' | 'right', ownerTeamId: string) {
    this.id = generateEntityId('supersheep');
    this.position = { x, y };
    this.facing = facing;
    this.ownerTeamId = ownerTeamId;
    this.velocity.x = facing === 'right' ? this.walkSpeed : -this.walkSpeed;
    this.randomJumpTimer = getGameRNG().nextFloat(0.3, 0.8);
    this.flyingAngle = facing === 'right' ? 0 : 180;
  }

  update(deltaTime: number, terrain?: { isSolid(x: number, y: number): boolean }): void {
    if (!this.active) return;

    this.lifetime -= deltaTime;
    if (this.lifetime <= 0) {
      this.explode();
      return;
    }

    if (this.isFlying) {
      this.updateFlying(deltaTime, terrain);
    } else {
      this.updateWalking(deltaTime, terrain);
    }
  }

  private updateFlying(
    deltaTime: number,
    terrain?: { isSolid(x: number, y: number): boolean },
  ): void {
    this.flightFuel -= deltaTime;
    if (this.flightFuel <= 0) {
      this.onFlyEnd?.();
      this.explode();
      return;
    }

    if (this.steeringLeft) {
      this.flyingAngle -= SUPER_SHEEP_TURN_RATE * deltaTime;
    }
    if (this.steeringRight) {
      this.flyingAngle += SUPER_SHEEP_TURN_RATE * deltaTime;
    }

    while (this.flyingAngle > 180) this.flyingAngle -= 360;
    while (this.flyingAngle < -180) this.flyingAngle += 360;

    this.facing = Math.abs(this.flyingAngle) < 90 ? 'right' : 'left';

    const angleRad = (this.flyingAngle * Math.PI) / 180;
    this.velocity.x = Math.cos(angleRad) * SUPER_SHEEP_FLIGHT_SPEED;
    this.velocity.y = Math.sin(angleRad) * SUPER_SHEEP_FLIGHT_SPEED;

    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;

    if (terrain) {
      const centerX = this.position.x + 8;
      const centerY = this.position.y + 6;
      if (terrain.isSolid(centerX, centerY)) {
        this.onFlyEnd?.();
        this.explode();
        return;
      }
    }
  }

  private updateWalking(
    deltaTime: number,
    terrain?: { isSolid(x: number, y: number): boolean },
  ): void {
    if (!this.isGrounded) {
      this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
    } else {
      this.velocity.x = this.facing === 'right' ? this.walkSpeed : -this.walkSpeed;

      this.randomJumpTimer -= deltaTime;
      if (this.randomJumpTimer <= 0 && this.jumpCooldown <= 0) {
        this.velocity.y = -this.jumpVelocity * getGameRNG().nextFloat(0.5, 1.0);
        this.isGrounded = false;
        this.jumpCooldown = 0.2;
        this.randomJumpTimer = getGameRNG().nextFloat(0.5, 1.5);
        this.onBaa?.();
      }
    }

    const width = 16;
    const height = 12;
    const maxClimb = 6;

    if (this.velocity.x !== 0 && terrain && this.isGrounded) {
      const moveX = this.velocity.x * deltaTime;
      const nextX = this.position.x + moveX;
      const edgeX = moveX > 0 ? nextX + width : nextX;

      let moved = false;
      for (let climbY = 0; climbY <= maxClimb; climbY++) {
        const testY = this.position.y - climbY;
        const blocked =
          terrain.isSolid(edgeX, testY + 2) || terrain.isSolid(edgeX, testY + height / 2);

        if (!blocked) {
          this.position.x = nextX;
          this.position.y = testY;

          for (let dropY = 0; dropY <= maxClimb + 2; dropY++) {
            const feetY = this.position.y + height;
            if (terrain.isSolid(this.position.x + width / 2, feetY + dropY)) {
              this.position.y = this.position.y + dropY;
              break;
            }
          }
          moved = true;
          break;
        }
      }

      if (!moved) {
        this.facing = this.facing === 'right' ? 'left' : 'right';
        this.velocity.x = -this.velocity.x;
      }
    } else if (this.velocity.x !== 0 && terrain && !this.isGrounded) {
      const moveX = this.velocity.x * deltaTime;
      this.position.x += moveX;
    }

    this.position.y += this.velocity.y * deltaTime;

    if (this.jumpCooldown > 0) {
      this.jumpCooldown -= deltaTime;
    }
  }

  activateFlight(): void {
    if (this.isFlying) {
      this.explode();
      return;
    }
    this.isFlying = true;
    this.isGrounded = false;
    this.flyingAngle = this.facing === 'right' ? -45 : -135;
    this.onFlyStart?.();
    this.onBaa?.();
  }

  steerLeft(): void {
    this.steeringLeft = true;
  }

  steerRight(): void {
    this.steeringRight = true;
  }

  applyTerrainPhysics(terrain: { isSolid(x: number, y: number): boolean }): void {
    if (!this.active || this.isFlying) return;

    const width = 16;
    const height = 12;
    const feetY = this.position.y + height;

    if (this.isEmbeddedInTerrain(width, height, terrain)) {
      this.escapeFromTerrain(width, height, terrain);
    }

    if (this.velocity.y >= 0) {
      for (let dy = 0; dy <= 5; dy++) {
        if (terrain.isSolid(this.position.x + width / 2, feetY + dy)) {
          this.position.y = feetY + dy - height;

          if (this.velocity.y > 100) {
            this.velocity.y = -this.velocity.y * this.bounciness;
            this.onBaa?.();
          } else {
            this.velocity.y = 0;
            this.isGrounded = true;
          }
          return;
        }
      }
    }

    const hasGroundBelow =
      terrain.isSolid(this.position.x + 2, feetY + 1) ||
      terrain.isSolid(this.position.x + width / 2, feetY + 1) ||
      terrain.isSolid(this.position.x + width - 2, feetY + 1);
    if (!hasGroundBelow) {
      this.isGrounded = false;
    }
  }

  checkWaterCollision(waterLevel: number, worldHeight: number): boolean {
    const waterY = worldHeight - waterLevel;
    return this.position.y > waterY;
  }

  private isEmbeddedInTerrain(
    width: number,
    height: number,
    terrain: { isSolid(x: number, y: number): boolean },
  ): boolean {
    const checkPoints = [
      { x: this.position.x + 2, y: this.position.y + 2 },
      { x: this.position.x + width - 2, y: this.position.y + 2 },
      { x: this.position.x + 2, y: this.position.y + height / 2 },
      { x: this.position.x + width - 2, y: this.position.y + height / 2 },
      { x: this.position.x + width / 2, y: this.position.y + height / 2 },
    ];
    return checkPoints.some((p) => terrain.isSolid(p.x, p.y));
  }

  private escapeFromTerrain(
    width: number,
    height: number,
    terrain: { isSolid(x: number, y: number): boolean },
  ): void {
    const startX = this.position.x;
    const startY = this.position.y;

    for (let dist = 1; dist <= 40; dist++) {
      for (let angle = 0; angle < 360; angle += 45) {
        const rad = (angle * Math.PI) / 180;
        const testX = startX + Math.cos(rad) * dist;
        const testY = startY + Math.sin(rad) * dist;

        this.position.x = testX;
        this.position.y = testY;

        if (!this.isEmbeddedInTerrain(width, height, terrain)) {
          this.velocity.x = 0;
          this.velocity.y = 0;
          return;
        }
      }
    }

    this.position.x = startX;
    this.position.y = startY;
  }

  explode(): void {
    if (!this.active) return;
    this.active = false;
    this.onExplode?.(this.position.x + 8, this.position.y + 6);
  }

  destroy(): void {
    this.active = false;
  }

  detonate(): void {
    this.explode();
  }
}

export const WeaponSuperSheep: WeaponDefinition = {
  id: 'supersheep',
  name: 'Super Sheep [ALPHA]',
  icon: 'supersheep.png',
  category: 'thrown',
  maxAmmo: 1,
  maxUsesPerTurn: 1,
  powerCharging: false,
  timerAdjustable: false,
  showAimGuide: false,
  allowRetreat: false,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm } = ctx;

    const spawnX = worm.facing === 'right' ? worm.position.x + 20 : worm.position.x - 20;
    const spawnY = worm.position.y + 4;

    const superSheep = new SuperSheep(spawnX, spawnY, worm.facing, worm.teamId);

    return {
      success: true,
      entities: [superSheep],
      message: 'Super Sheep deployed!',
    };
  },
};

export {
  SUPER_SHEEP_FLIGHT_SPEED,
  SUPER_SHEEP_TURN_RATE,
  SUPER_SHEEP_MAX_FUEL,
  SUPER_SHEEP_EXPLOSION_RADIUS,
  SUPER_SHEEP_MAX_DAMAGE,
};
